import React, { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { KeyboardArrowDown } from '@mui/icons-material'
import {
  MCoach as MCoachIcon,
  SkillBites as SkillBitesIcon,
  Playlist as PlaylistIcon,
  BonusMaterial as BonusIcon,
  ContentHub as ContentHubIcon,
  Quizzes as QuizzesIcon
} from '../icons'
import { routes } from '../../../router/routes'
import { useSearchQuery } from '../../../pages/search-results/hooks'
import { useMindset } from '../../../components/mindset-agent/mindset-context'
import { ActionButton } from '../../action-buttons'
import useAccessByContext from './useAccessByContext'

/**
 * Custom hook for Paid Consumer App Toolbar actions
 * Provides navigation items for both desktop and mobile interfaces
 */
const usePaidConsumerActions = ({ onClose, onExploreToggle, className } = {}) => {
  const { addFilter } = useSearchQuery()
  const { openMindset } = useMindset()
  const { accessInContext } = useAccessByContext()

  const handleMCoachClick = useCallback((evt, source) => {
    openMindset({
      from: source || 'paid-consumer-nav'
    })
    if (onClose) onClose()
  }, [openMindset, onClose])

  const handleContentHubClick = useCallback(() => {
    if (onExploreToggle) {
      onExploreToggle()
    }
    // Don't close drawer immediately for mobile - let user interact with explore menu
  }, [onExploreToggle])

  const handleQuizzesClick = useCallback(() => {
    const quizUrl = addFilter(
      { search: 'Quiz', type: 'self-assessment,quiz' },
      { routeKey: 'serp', clear: true }
    )
    if (onClose) onClose()
    return quizUrl
  }, [addFilter, onClose])

  // Define all possible actions
  const allActions = [
    {
      id: 'mcoach-action',
      key: 'mcoach-action',
      component: ActionButton,
      props: {
        startIcon: <MCoachIcon />,
        label: 'Ask M:Coach',
        labelhover: 'Ask M:Coach',
        onClick: (evt) => handleMCoachClick(evt, 'paid-consumer-nav'),
        'data-test': 'appbar-mcoach-button',
        variant: 'link',
        className
      },
      permissions: {
        desktop: 'navmenu',
        mobile: 'menu',
        tablet: 'menu'
      },
      permissionKey: 'mcoach'
    },
    {
      id: 'skillbites-action',
      key: 'skillbites-action',
      component: ActionButton,
      props: {
        variant: 'link',
        component: Link,
        to: routes['skill-bites'].path,
        startIcon: <SkillBitesIcon />,
        label: 'Skill Bites',
        labelhover: 'Skill Bites',
        'data-test': 'appbar-skillbites-button',
        className
      },
      permissions: {
        desktop: 'navmenu',
        mobile: 'menu',
        tablet: 'menu'
      },
      permissionKey: 'skillbites'
    },
    {
      id: 'contenthub-action',
      key: 'contenthub-action',
      component: ActionButton,
      props: {
        startIcon: <ContentHubIcon />,
        endIcon: <KeyboardArrowDown />,
        label: 'Content Hub',
        labelhover: 'Content Hub',
        onClick: handleContentHubClick,
        'data-test': 'appbar-contenthub-button',
        variant: 'link',
        className
      },
      permissions: {
        desktop: 'navmenu',
        mobile: 'menu',
        tablet: 'menu'
      },
      permissionKey: 'contenthub'
    },
    {
      id: 'playlists-action',
      key: 'playlists-action',
      component: ActionButton,
      props: {
        component: Link,
        to: routes.playlists.path,
        startIcon: <PlaylistIcon />,
        label: 'Playlist',
        labelhover: 'Playlist',
        'data-test': 'appbar-playlist-button',
        variant: 'link',
        className
      },
      permissions: {
        desktop: 'navmenu',
        mobile: 'menu',
        tablet: 'menu'
      },
      permissionKey: 'playlists'
    },
    {
      id: 'quizzes-action',
      key: 'quizzes-action',
      component: ActionButton,
      props: {
        component: Link,
        to: addFilter({ search: 'Quiz', type: 'self-assessment,quiz' }, { routeKey: 'serp', clear: true }),
        startIcon: <QuizzesIcon />,
        label: 'Quizzes',
        labelhover: 'Quizzes',
        'data-test': 'appbar-quizzes-button',
        variant: 'link',
        className
      },
      permissions: {
        desktop: 'navmenu',
        mobile: 'menu',
        tablet: 'menu'
      },
      permissionKey: 'quizzes'
    },
    {
      id: 'bonus-action',
      key: 'bonus-action',
      component: ActionButton,
      props: {
        component: Link,
        to: routes['bonus-material'].path,
        startIcon: <BonusIcon />,
        label: 'Bonus',
        labelhover: 'Bonus',
        'data-test': 'appbar-bonus-button',
        variant: 'link',
        className
      },
      permissions: {
        desktop: 'navmenu',
        mobile: 'menu',
        tablet: 'menu'
      },
      permissionKey: 'bonus'
    }
  ]

  /**
   * Filter actions based on permissions for a given context
   * @param {string} context - The context (navmenu for desktop, menu for mobile/tablet)
   * @returns {Array} Filtered array of action objects
   */
  const filterActions = (context) => {
    return allActions.filter(action => {
      const { permissions, permissionKey } = action

      // Check if action has permission configuration for this context
      if (!permissions[context]) return false

      // Use accessInContext to check if user has permission
      return accessInContext(permissions[context], permissionKey)
    })
  }

  /**
   * Get rendered action components for a given context
   * @param {string} context - The context (navmenu for desktop, menu for mobile/tablet)
   * @returns {Array} Array of React components
   */
  const getActionComponents = (context) => {
    const filteredActions = filterActions(context)

    return filteredActions.map(action => {
      const { component: Component, props, key } = action
      return <Component key={key} {...props} />
    })
  }

  /**
   * Get action data for custom rendering (useful for mobile drawer)
   * @param {string} context - The context (navmenu for desktop, menu for mobile/tablet)
   * @returns {Object} Object with action data keyed by action ID
   */
  const getActionData = (context) => {
    const filteredActions = filterActions(context)
    const actionData = {}

    filteredActions.forEach(action => {
      const actionKey = action.permissionKey
      actionData[actionKey] = {
        id: action.id,
        label: action.props.label,
        icon: action.props.startIcon.type, // Get the icon component
        onClick: action.props.onClick || (() => {}),
        testId: action.permissionKey,
        to: action.props.to
      }
    })

    return actionData
  }

  return {
    filterActions,
    getActionComponents,
    getActionData,
    handleMCoachClick,
    handleContentHubClick,
    handleQuizzesClick
  }
}

export default usePaidConsumerActions
